<template>
  <SheetModal
    class="AddCourtModalView"
    data-theme="light"
    aria-label="Add a court"
    :aria-busy="isRequesting"
    @close="$emit('close')"
  >
    <form
      class="AddCourtModalView__form"
      @submit.prevent="handleSubmit"
    >
      <fieldset
        class="AddCourtModalView__fieldset"
        :disabled="isRequesting"
      >
        <SheetHeader
          title="Add a court"
          excludeSubmitButton
          @cancel="$emit('close')"
        />

        <div
          class="AddCourtModalView__search"
        >
          <FormInput
            ref="searchInputRef"
            v-model="searchInput"
            name="search"
            type="search"
            autocomplete="off"
            ariaLabel="Search for a court"
            enterkeyhint="search"
            :placeholder="placeholderCourt.name"
            @update:modelValue="handleSearchInput"
          />
        </div>

        <ul
          v-if="courtSuggestions.length > 0"
          class="AddCourtModalView__suggestionList"
        >
          <li
            v-for="suggestion in courtSuggestions"
            :key="suggestion.placeId"
            class="AddCourtModalView__suggestionItem"
          >
            <button
              type="button"
              class="AddCourtModalView__suggestionButton"
              @click="selectedSuggestion = suggestion"
            >
              <div class="AddCourtModalView__suggestionIconContainer">
                <Basketball
                  v-if="suggestion.court"
                  class="AddCourtModalView__suggestionIcon"
                />
                <Icon
                  v-else
                  name="location"
                />
              </div>
              <div class="AddCourtModalView__suggestionContent">
                <span class="AddCourtModalView__suggestionName">{{ suggestion.name }}</span>
                <span
                  v-if="suggestion.address"
                  class="AddCourtModalView__suggestionAddress"
                >{{ suggestion.address }}</span>
              </div>
            </button>
          </li>
        </ul>

        <div
          v-else
          class="AddCourtModalView__emptyStateContainer"
        >
          <Transition>
            <EmptyState
              v-if="!trimmedSearchInput"
              class="AddCourtModalView__emptyState"
              iconName="location"
              title="Search for your court"
              subtitle="Enter the name or address above"
            />
            <EmptyState
              v-else-if="isFetchingSuggestions"
              class="AddCourtModalView__emptyState"
              iconName="locationMap"
              title="Searching Google Maps…"
              subtitle="This may take a few seconds"
            />
            <EmptyState
              v-else
              class="AddCourtModalView__emptyState"
              iconName="locationX"
              title="No courts found"
              subtitle="Try searching for a different court name or address."
            />
          </Transition>
        </div>
      </fieldset>
    </form>

    <Teleport to="body">
      <Transition>
        <PlaceModalView
          v-if="selectedSuggestion"
          :modelValue="selectedSuggestion"
          :sessionToken="courtSessionToken"
          @select="handleCourtSelect"
          @close="selectedSuggestion = null"
        />
      </Transition>
    </Teleport>
  </SheetModal>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import EmptyState from "@/components/EmptyState.vue";
import FormInput from "@/components/FormInput.vue";
import Icon from "@/components/Icon.vue";
import SheetHeader from "@/components/SheetHeader.vue";
import SheetModal from "@/components/SheetModal.vue";
import useCourtSuggestions from "@/composables/useCourtSuggestions";
import useAuthStore from "@/stores/useAuthStore";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore";
import { CourtSuggestion, SuggestedCourt } from "@shared/lib/api/courts.types";
import { courtMap } from "@shared/lib/constants/courts";
import { Theme } from "@shared/lib/constants/themes";
import { storeToRefs } from "pinia";
import { ComponentPublicInstance, computed, onBeforeMount, ref, watch } from "vue";
import PlaceModalView from "../modals/PlaceModalView.vue";

const emit = defineEmits<{
  close: [],
}>();

const {
  isTransitioning,
} = defineProps<{
  isTransitioning?: boolean,
}>();

const {
  theme,
} = storeToRefs(useAuthStore());

const {
  fetchCrewMemberships,
} = useCurrentPlayerStore();

const isRequesting = ref(true);
const searchInput = ref("");
const selectedSuggestion = ref<CourtSuggestion | null>(null);
const searchInputRef = ref<ComponentPublicInstance<typeof FormInput> | null>(null);

const {
  isFetchingSuggestions,
  courtSuggestions,
  courtSessionToken,
  fetchCourtSuggestions,
} = useCourtSuggestions();

onBeforeMount(async () => {
  try {
    await fetchCrewMemberships();
  } catch (err) {
    console.error(err);
  }

  isRequesting.value = false;
});

watch(() => isTransitioning, () => {
  if (!isTransitioning) {
    searchInputRef.value?.focus();
  }
});

const trimmedSearchInput = computed(() => {
  return searchInput.value.trim();
});

const placeholderCourt = computed(() => {
  return courtMap.get(theme.value as keyof typeof Theme) || {
    name: "Court name",
    address: "Street address",
  };
});

function handleSearchInput (value: string | number | null) {
  fetchCourtSuggestions(String(value ?? ""));
}

function handleSubmit () {
  if (document.activeElement instanceof HTMLInputElement) {
    document.activeElement.blur();
  }
}

function handleCourtSelect (_court: SuggestedCourt) {
  selectedSuggestion.value = null;
  emit("close");
}
</script>

<style lang="scss" scoped>
.AddCourtModalView__form {
  --transitionDuration: 350ms;
  position: absolute;
  inset: 0;
}

.AddCourtModalView__fieldset {
  display: grid;
  grid: min-content min-content auto / auto;
  height: 100%;
}

.AddCourtModalView__content {
  position: relative;

  min-height: 0;
  padding-bottom: var(--safeAreaInsetBottom);
  overflow-y: auto;
}

.AddCourtModalView__fieldGrid {
  display: grid;
  gap: 24px 16px;
  padding: 24px 16px;

  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.AddCourtModalView__search {
  position: relative;
  padding: 0 12px 12px;
  background-color: var(--themeColorSecondary);
}

.AddCourtModalView__emptyStateContainer {
  position: relative;

  // TODO: make this dynamic based on the height of the screen
  height: 100%;
  max-height: 360px;
  padding-bottom: var(--safeAreaInsetBottom);
}

.AddCourtModalView__emptyState {
  position: absolute;
  inset: 0;

  transition: 250ms;
  transition-property: opacity, transform;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  &.v-enter-from {
    transform: translateX(24px);
  }

  &.v-leave-to {
    transform: translateX(-24px);
  }
}

.AddCourtModalView__suggestionList {
  display: grid;
  gap: 1px;
  align-content: start;

  background-color: var(--themeColorNeutral0);
}

.AddCourtModalView__suggestionItem {
  display: block;
  outline: 1px solid var(--themeColorNeutral100);
}

.AddCourtModalView__suggestionButton {
  display: grid;
  grid-template-columns: min-content auto;
  gap: 12px;
  align-items: center;
  width: 100%;
  padding: 16px;

  background-color: var(--themeColorWhite);

  text-align: left;

  transition: var(--transitionDuration);
  transition-property: background-color;

  &:disabled {
    background-color: transparent;
  }
}

.AddCourtModalView__suggestionIconContainer {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;

  border-radius: 50%;

  transition: var(--transitionDuration);
  transition-property: filter, color, opacity;

  &:has(.Basketball) {
    border: solid 3px var(--themeColorPrimary);
  }

  &:has(.Icon) {
    background-color: var(--themeColorNeutral100);
    color: var(--themeColorNeutral600);
  }

  .AddCourtModalView__suggestionButton:disabled & {
    color: var(--themeDarkTransparent300);

    &:has(.Basketball) {
      opacity: 0.5;
      filter: grayscale(100%);
    }
  }
}

.AddCourtModalView__suggestionIcon {
  width: 36px;
  height: 36px;
}

.AddCourtModalView__suggestionContent {
  position: relative;
  top: var(--fontSafariOffset);

  display: grid;
  gap: 8px;
}

.AddCourtModalView__suggestionName {
  overflow: hidden;

  color: var(--themeColorInputForeground);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;

  transition: color var(--transitionDuration);

  .AddCourtModalView__suggestionButton:disabled & {
    color: var(--themeDarkTransparent500);
  }
}

.AddCourtModalView__suggestionAddress {
  overflow: hidden;

  color: var(--themeColorNeutral500);

  font-size: 14px;
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;

  transition: color var(--transitionDuration);

  .AddCourtModalView__suggestionButton:disabled & {
    color: var(--themeDarkTransparent500);
  }
}
</style>
